import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import styles from './AccordionBase.module.css';
import { useControllableState } from '../../../hooks';
import { type Nullable } from '../../../types';
import { cn, useElementHeight } from '../../../utils';

export interface AccordionConfig {
  /**
   * Ukontrollerttilstand: om utvidbare delen skal være utvidet ved første render.
   */
  isInitiallyExpanded?: boolean;
  /**
   * Kontrollert tilstand: om utvidbare delen er utvidet.
   */
  isExpanded?: boolean;
  /**
   * Callback når tilstanden endres.
   */
  onChange?: (expanded: boolean) => void;
  /**
   * Custom id for accordion; delene får i tillegg suffix `'header'` og `'body'`.
   */
  id?: string;
}

export interface UseAccordionHeaderProps {
  /**
   * `onClick` som kontrollerer lukking og åpning av accordion, og eventuelt `onChange` satt i config
   */
  onClick: () => void;
  /**
   * Nativ `aria-expanded`.
   */
  'aria-expanded': boolean;
  /**
   * Nativ `aria-controls`.
   */
  'aria-controls': string;
  /**
   * Id, settes for riktig bruk av aria-props.
   */
  id: string;
}

export interface UseAccordionBodyProps {
  /**
   * Id, settes for riktig bruk av aria-props.
   */
  id: string;
  /**
   * Nativ `role`. Gir innholdet en rolle tilgjengelig for skjermlesere.
   */
  role: string;
  /**
   * Nativ `aria-labelledby`.
   */
  'aria-labelledby': string;
  /**
   * Nativ `aria-hidden`.
   */
  'aria-hidden': boolean;
  /**
   * Høyde som skal settes i CSS for animasjon.
   */
  height?: number;
  /**
   * Klassenavn for innhold-wrapper; håndterer animasjon og lukking.
   */
  className?: string;
}

export interface AccordionState {
  /**
   * Id, settes for riktig bruk av aria-props.
   */
  id: string;
  /**
   * Om accordion er utvided.
   */
  isExpanded: boolean;
  /**
   * State funksjon som setter `isExpanded` ved behov.
   */
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  /**
   * Funksjon som åpner accordion. Brukes ved behov.
   */
  openAccordion: () => void;
  /**
   * Funksjon som lukker accordion. Brukes ved behov.
   */
  closeAccordion: () => void;
  /**
   * Funksjon som toggler accordion. Brukes ved behov.
   */
  toggleExpanded: () => void;
  /**
   * Props som settes på header-elementet.
   */
  headerProps: UseAccordionHeaderProps;
  /**
   * Props som settes på innholds-elementet.
   */
  bodyProps: UseAccordionBodyProps;
  /**
   * Ref som settes på wrapper inni innholds-elementet; Sørger for animasjon.
   */
  bodyContentRef: RefObject<HTMLDivElement | null>;
}

/**
 * Implementerer trekkspill-mønsteret; header som utvider og trekker sammen et panel. Håndterer oppførsel, UU og visuelle effekter.
 * @returns {AccordionState} accordion state og props å bruke på acccordion-elementer: header og body.
 * @example
 * ```tsx
 * function MyComponent() {
 *
 *  const { isExpanded, bodyContentRef, headerProps, bodyProps } = useAccordion({});
 *  const { height, ...restBodyProps } = bodyProps;
 *
 *  return (
 *    <>
 *      <button {...headerProps}>
 *        {isExpanded ? 'Skjul' : 'Vis'} innhold
 *      </button>
 *      <div style={{height}} {...restBodyProps}>
 *        <div ref={bodyContentRef}>
 *          innhold
 *        </div>
 *      </div>
 *    </>
 *  )
 * });
 * ```
 */

export const useAccordion = ({
  isInitiallyExpanded = false,
  isExpanded: isExpandedProp,
  onChange,
  id,
}: AccordionConfig): AccordionState => {
  const [isExpanded, setIsExpanded] = useControllableState<boolean>({
    onChange,
    defaultValue: isInitiallyExpanded,
    value: isExpandedProp,
  });

  const generatedId = useId();
  const accordionId = id ?? `${generatedId}-accordion`;

  const openAccordion = useCallback(() => setIsExpanded(true), [setIsExpanded]);
  const closeAccordion = useCallback(
    () => setIsExpanded(false),
    [setIsExpanded],
  );

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, [setIsExpanded]);

  const bodyContentRef = useRef<HTMLDivElement>(null);

  const [animate, setAnimate] = useState(false);

  const height = useElementHeight(bodyContentRef.current);

  const [initialExpandedHeight, setIntialExpandedHeight] =
    useState<Nullable<number>>(null);

  useLayoutEffect(() => {
    // For å unngå initiell animasjon dersom Accordion er satt til å være åpen som default.
    if (bodyContentRef.current && isExpanded) {
      setIntialExpandedHeight(bodyContentRef.current.scrollHeight);
    }
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setAnimate(true);
    });

    return () => cancelAnimationFrame(raf);
  }, []);

  const headerProps: UseAccordionHeaderProps = {
    id: `${accordionId}-header`,
    onClick: toggleExpanded,
    'aria-expanded': isExpanded,
    'aria-controls': `${accordionId}-body`,
  };

  const bodyProps: UseAccordionBodyProps = {
    id: `${accordionId}-body`,
    role: 'region',
    'aria-labelledby': `${accordionId}-header`,
    'aria-hidden': !isExpanded,
    className: cn(
      styles.body,
      !isExpanded && styles['body--hidden'],
      animate && styles['body--animated'],
    ),
    height: height ?? initialExpandedHeight ?? 0,
  };

  return {
    id: accordionId,
    isExpanded,
    setIsExpanded,
    openAccordion,
    closeAccordion,
    toggleExpanded,
    bodyContentRef,
    headerProps,
    bodyProps,
  };
};
